// src/components/manager/shared/ClientFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit, Trash2, Search, Filter, Mail, Phone, MapPin,
  Calendar, DollarSign, User, Building2, Star, Tag, Clock, X,
  TrendingUp, AlertCircle, CheckCircle, FileText, History
} from 'lucide-react';
import { firestoreService } from '../../../services/firestoreService';
import { organisationService } from '../../../services/organisationService';

const CLIENT_TYPES = {
  individual: { label: 'Individual', color: 'blue', icon: User },
  business: { label: 'Business', color: 'green', icon: Building2 },
  organization: { label: 'Organization', color: 'purple', icon: Users },
  government: { label: 'Government', color: 'indigo', icon: Building2 }
};

const CLIENT_STATUS = {
  active: { label: 'Active', color: 'green', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'gray', icon: Clock },
  potential: { label: 'Potential', color: 'yellow', icon: Star },
  former: { label: 'Former', color: 'red', icon: AlertCircle }
};

const PRIORITY_LEVELS = {
  low: { label: 'Low', color: 'gray' },
  medium: { label: 'Medium', color: 'blue' },
  high: { label: 'High', color: 'orange' },
  critical: { label: 'Critical', color: 'red' }
};

export const ClientFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    type: 'individual',
    status: 'active',
    priority: 'medium',
    companyName: '',
    jobTitle: '',
    
    // Contact Information
    email: '',
    phone: '',
    alternatePhone: '',
    website: '',
    
    // Address
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'South Africa'
    },
    
    // Business Details
    industry: '',
    companySize: '',
    revenue: '',
    
    // Relationship
    source: '',
    referredBy: '',
    assignedTo: '',
    relationshipStart: '',
    lastContact: '',
    nextFollowUp: '',
    
    // Service Details
    servicesUsed: [],
    totalValue: '',
    averageOrderValue: '',
    lastOrder: '',
    
    // Communication Preferences
    preferredContact: 'email',
    timezone: 'Africa/Johannesburg',
    language: 'English',
    
    // Notes and Tags
    notes: '',
    tags: [],
    
    // Custom Fields
    customFields: {}
  });

  useEffect(() => {
    loadClientData();
  }, [bookId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'clients');
      setClients(data?.clients || []);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveClientData = async (updatedClients) => {
    try {
      const stats = {
        totalClients: updatedClients.length,
        activeClients: updatedClients.filter(c => c.status === 'active').length,
        potentialClients: updatedClients.filter(c => c.status === 'potential').length,
        totalValue: updatedClients.reduce((sum, client) => sum + (parseFloat(client.totalValue) || 0), 0),
        averageValue: updatedClients.length > 0 
          ? updatedClients.reduce((sum, client) => sum + (parseFloat(client.totalValue) || 0), 0) / updatedClients.length 
          : 0
      };

      await organisationService.updateManagementFeatureData(bookId, 'clients', {
        clients: updatedClients,
        lastUpdated: new Date(),
        ...stats
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error saving client data:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const clientData = {
        ...formData,
        id: editingClient?.id || Date.now().toString(),
        createdAt: editingClient?.createdAt || new Date(),
        updatedAt: new Date(),
        totalValue: formData.totalValue ? parseFloat(formData.totalValue) : 0,
        averageOrderValue: formData.averageOrderValue ? parseFloat(formData.averageOrderValue) : 0,
        tags: Array.isArray(formData.tags) ? formData.tags : 
              typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()) : [],
        servicesUsed: Array.isArray(formData.servicesUsed) ? formData.servicesUsed :
                     typeof formData.servicesUsed === 'string' ? formData.servicesUsed.split(',').map(s => s.trim()) : []
      };

      let updatedClients;
      if (editingClient) {
        updatedClients = clients.map(client => 
          client.id === editingClient.id ? clientData : client
        );
      } else {
        updatedClients = [...clients, clientData];
      }

      await saveClientData(updatedClients);
      setClients(updatedClients);
      handleCloseForm();
    } catch (error) {
      alert('Error saving client: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      const updatedClients = clients.filter(client => client.id !== clientId);
      await saveClientData(updatedClients);
      setClients(updatedClients);
    } catch (error) {
      alert('Error deleting client: ' + error.message);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      ...client,
      tags: Array.isArray(client.tags) ? client.tags.join(', ') : client.tags || '',
      servicesUsed: Array.isArray(client.servicesUsed) ? client.servicesUsed.join(', ') : client.servicesUsed || ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData({
      name: '', type: 'individual', status: 'active', priority: 'medium',
      companyName: '', jobTitle: '', email: '', phone: '', alternatePhone: '',
      website: '', address: { street: '', city: '', province: '', postalCode: '', country: 'South Africa' },
      industry: '', companySize: '', revenue: '', source: '', referredBy: '',
      assignedTo: '', relationshipStart: '', lastContact: '', nextFollowUp: '',
      servicesUsed: [], totalValue: '', averageOrderValue: '', lastOrder: '',
      preferredContact: 'email', timezone: 'Africa/Johannesburg', language: 'English',
      notes: '', tags: [], customFields: {}
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || client.type === filterType;
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-7 w-7 text-blue-600" />
            Client Management
          </h3>
          <p className="text-gray-600 mt-1">Manage customers, members, students, or constituents</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Client
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Clients</p>
              <p className="text-2xl font-bold text-green-600">
                {clients.filter(c => c.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Potential</p>
              <p className="text-2xl font-bold text-yellow-600">
                {clients.filter(c => c.status === 'potential').length}
              </p>
            </div>
            <Star className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-purple-600">
                R{clients.reduce((sum, client) => sum + (parseFloat(client.totalValue) || 0), 0).toLocaleString()}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {Object.entries(CLIENT_TYPES).map(([key, type]) => (
            <option key={key} value={key}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {Object.entries(CLIENT_STATUS).map(([key, status]) => (
            <option key={key} value={key}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map(client => {
          const typeConfig = CLIENT_TYPES[client.type] || CLIENT_TYPES.individual;
          const statusConfig = CLIENT_STATUS[client.status] || CLIENT_STATUS.active;
          const priorityConfig = PRIORITY_LEVELS[client.priority] || PRIORITY_LEVELS.medium;
          const TypeIcon = typeConfig.icon;
          const StatusIcon = statusConfig.icon;
          
          return (
            <div key={client.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Client Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${typeConfig.color}-100`}>
                    <TypeIcon className={`h-5 w-5 text-${typeConfig.color}-600`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{client.name}</h4>
                    <p className="text-sm text-gray-600">
                      {client.companyName || typeConfig.label}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full bg-${priorityConfig.color}-500`} title={`${priorityConfig.label} Priority`} />
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.label}
                  </div>
                </div>
              </div>

              {/* Client Details */}
              <div className="space-y-2 mb-4">
                {client.jobTitle && (
                  <p className="text-sm text-gray-600">{client.jobTitle}</p>
                )}
                
                {client.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${client.email}`} className="hover:text-blue-600">
                      {client.email}
                    </a>
                  </div>
                )}

                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${client.phone}`} className="hover:text-blue-600">
                      {client.phone}
                    </a>
                  </div>
                )}

                {(client.address.city || client.address.province) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {[client.address.city, client.address.province].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>

              {/* Business Metrics */}
              {(client.totalValue || client.lastContact) && (
                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                  {client.totalValue && (
                    <div>
                      <p className="text-xs text-gray-500">Total Value</p>
                      <p className="text-sm font-medium">R{parseFloat(client.totalValue).toLocaleString()}</p>
                    </div>
                  )}
                  {client.lastContact && (
                    <div>
                      <p className="text-xs text-gray-500">Last Contact</p>
                      <p className="text-sm font-medium">{new Date(client.lastContact).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {client.tags && client.tags.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(client.tags) ? client.tags : client.tags.split(','))
                      .slice(0, 3)
                      .map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {tag.trim()}
                        </span>
                      ))}
                    {(Array.isArray(client.tags) ? client.tags : client.tags.split(',')).length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{(Array.isArray(client.tags) ? client.tags : client.tags.split(',')).length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Follow-up Alert */}
              {client.nextFollowUp && new Date(client.nextFollowUp) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-yellow-800">
                    <Calendar className="h-4 w-4" />
                    Follow-up due: {new Date(client.nextFollowUp).toLocaleDateString()}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  {client.assignedTo && (
                    <span className="text-xs text-gray-500">Assigned: {client.assignedTo}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setSelectedClient(client)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                    title="View details"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(client)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first client.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Client
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  {editingClient ? 'Edit Client' : 'Add Client'}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(CLIENT_TYPES).map(([key, type]) => (
                        <option key={key} value={key}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(CLIENT_STATUS).map(([key, status]) => (
                        <option key={key} value={key}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(PRIORITY_LEVELS).map(([key, priority]) => (
                        <option key={key} value={key}>{priority.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                    <input
                      type="text"
                      value={formData.jobTitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Phone</label>
                      <input
                        type="tel"
                        value={formData.alternatePhone}
                        onChange={(e) => setFormData(prev => ({ ...prev, alternatePhone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={formData.address.street}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, street: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
                      <input
                        type="text"
                        value={formData.address.province}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, province: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Business Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                      <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Total Value (R)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.totalValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, totalValue: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                      <input
                        type="text"
                        value={formData.assignedTo}
                        onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Account manager or team member"
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Timeline</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Start</label>
                      <input
                        type="date"
                        value={formData.relationshipStart}
                        onChange={(e) => setFormData(prev => ({ ...prev, relationshipStart: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Contact</label>
                      <input
                        type="date"
                        value={formData.lastContact}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastContact: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Next Follow-up</label>
                      <input
                        type="date"
                        value={formData.nextFollowUp}
                        onChange={(e) => setFormData(prev => ({ ...prev, nextFollowUp: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="border-t pt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Comma-separated tags (e.g. VIP, referral, high-value)"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Additional notes, preferences, or important information..."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {editingClient ? 'Update Client' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};